import { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { insertTimeLogSchema } from '@/lib/shared-schema';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getISODate } from '@/lib/date-utils';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Calendar as CalendarIcon, X } from 'lucide-react';
import { formLabelStyles } from '@/components/ui/form-styles';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';

// Define engagement interface
interface Engagement {
  id: number;
  clientName: string;
  projectName: string;
  hourlyRate: string;
  startDate: string;
  endDate: string;
  status: string;
  engagementType?: string;
}

// Extend the time log schema with additional validation
const formSchema = insertTimeLogSchema
  .omit({
    userId: true // We'll add this on the server
  })
  .extend({
    engagementId: z.number().min(1, 'Please select an engagement'),
    date: z.string().optional(), // Make optional - we'll validate based on mode
    hours: z.number().min(0.25, 'Hours must be at least 0.25').max(8, 'Hours cannot exceed 8'),
    description: z.string().optional(), // Make description optional
  });

type FormValues = z.infer<typeof formSchema>;

interface TimeLogModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  timeLog?: any; // For editing existing time log
  onSuccess: () => void;
  preselectedEngagementId?: number;
}

interface TimeLogEntry {
  date: string;
  hours: number;
  description: string | null;
}

const TimeLogModal = ({
  open,
  onOpenChange,
  timeLog,
  onSuccess,
  preselectedEngagementId,
}: TimeLogModalProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedEngagementRate, setSelectedEngagementRate] = useState<string | null>(null);
  const [selectedEngagementType, setSelectedEngagementType] = useState<string | null>(null);
  const [multipleEntries, setMultipleEntries] = useState(false);
  const [createdLogs, setCreatedLogs] = useState<TimeLogEntry[]>([]);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);

  const isEditMode = !!timeLog;

  // Get the fetch status
  const { data: engagements = [], isLoading: isLoadingEngagements, isError: isEngagementsError } = useQuery<Engagement[]>({
    queryKey: ['/api/engagements/active'],
    queryFn: async () => {
      console.log('Fetching active engagements for time log modal');
      try {
        // Add authentication debugging
        const authCheckResponse = await fetch('/api/user');
        const isAuthenticated = authCheckResponse.ok;
        console.log('Authentication check:', isAuthenticated);
        if (!isAuthenticated) {
          console.warn('User not authenticated when trying to fetch engagements');
        }

        const response = await fetch('/api/engagements/active', {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });

        console.log('Active engagements API response status:', response.status);

        if (!response.ok) {
          console.error('Failed to fetch active engagements:', response.status);
          const errorText = await response.text().catch(() => 'No error text available');
          console.error('Error response:', errorText);
          return [];
        }

        const data = await response.json();
        console.log('Received active engagements data:', data);

        if (!Array.isArray(data)) {
          console.error('Expected array of engagements but received:', typeof data);
          return [];
        }

        if (data.length === 0) {
          console.warn('Received empty array of engagements');

          // For debugging, try fetching all engagements to see if any exist
          const allEngagementsResponse = await fetch('/api/engagements');
          if (allEngagementsResponse.ok) {
            const allEngagements = await allEngagementsResponse.json();
            console.log(`All engagements endpoint returned ${allEngagements.length} engagements`);
          }
        }

        return data;
      } catch (error) {
        console.error('Error fetching engagements:', error);
        return []; // Return empty array instead of throwing
      }
    },
    enabled: open,
  });

  // Check if we have no engagements after loading completed
  const noEngagements = !isLoadingEngagements && engagements.length === 0;

  // Debug log to check engagements data
  useEffect(() => {
    if (engagements) {
      console.log(`TimeLogModal: Received ${engagements.length} active engagements`);
      console.log('Engagements data:', engagements);
    }
  }, [engagements]);

  // Get engagement ID from either direct property or nested engagement object
  const getEngagementId = () => {
    if (!timeLog) return preselectedEngagementId || 0;

    // Handle both data structures: flat or nested engagement
    if (timeLog.engagementId !== undefined) {
      return timeLog.engagementId;
    } else if (timeLog.engagement && timeLog.engagement.id !== undefined) {
      return timeLog.engagement.id;
    }

    return 0;
  };

  // Debug log to check what's happening with engagement ID
  console.log('TimeLog data:', timeLog);
  console.log('Engagement ID from getEngagementId():', getEngagementId());

  // Format date safely to handle date strings or Date objects properly
  const formatDateInput = (dateInput: any): string => {
    try {
      // If it's already a string in yyyy-MM-dd format, return it
      if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
        return dateInput;
      }

      // Otherwise, try to create a Date object and format it
      const date = new Date(dateInput);
      if (isNaN(date.getTime())) {
        return new Date().toISOString().split('T')[0]; // Fallback to today if invalid
      }

      // Fix timezone issue: Create a new date using local date components
      // This ensures the date is not affected by timezone conversion
      const localDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate() + 1 // Add one day to compensate for timezone shift
      );

      console.log('Original date input:', dateInput, 'Corrected date:', localDate);

      // Format as YYYY-MM-DD
      const year = localDate.getFullYear();
      const month = String(localDate.getMonth() + 1).padStart(2, '0');
      const day = String(localDate.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;

    } catch (e) {
      console.error("Error formatting date:", e);
      return new Date().toISOString().split('T')[0]; // Fallback to today
    }
  };

  // Initialize form with default values or existing time log
  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: timeLog
      ? {
        date: formatDateInput(timeLog.date),
        hours: timeLog.hours ? Number(timeLog.hours) : 0,
        description: timeLog.description === null || timeLog.description === undefined ? '' : timeLog.description,
        engagementId: getEngagementId(),
      }
      : {
        engagementId: preselectedEngagementId || undefined,
        date: new Date().toISOString().split('T')[0],
        hours: 0,
        description: '',
      },
  });

  // Debug log the form initialization
  console.log('Form initialized with:', {
    timeLog,
    defaultDescription: timeLog ? (timeLog.description === null || timeLog.description === undefined ? '' : timeLog.description) : '',
    defaultValues: timeLog
      ? {
        date: formatDateInput(timeLog.date),
        hours: timeLog.hours ? Number(timeLog.hours) : 0,
        description: timeLog.description === null || timeLog.description === undefined ? '' : timeLog.description,
        engagementId: getEngagementId(),
      }
      : {
        engagementId: preselectedEngagementId || undefined,
        date: new Date().toISOString().split('T')[0],
        hours: 0,
        description: '',
      },
  });

  // Watch engagementId for debugging
  const engagementIdValue = watch('engagementId');
  console.log('Watched engagementId value:', engagementIdValue);

  // Update form fields when modal opens with timeLog data
  useEffect(() => {
    if (open && timeLog) {
      // Reset form with time log data using the safe formatter
      console.log('Resetting form with time log data:', timeLog);
      console.log('Description from API:', timeLog.description);

      reset({
        date: formatDateInput(timeLog.date),
        hours: timeLog.hours ? Number(timeLog.hours) : 0,
        description: timeLog.description === null || timeLog.description === undefined ? '' : timeLog.description,
        engagementId: getEngagementId(),
      });

      console.log('Form reset complete');
    }
  }, [open, timeLog, reset]);

  // Find and set the rate when an engagement is selected
  useEffect(() => {
    if (open && engagements && engagements.length > 0) {
      // Get engagementId from either direct property or nested engagement object
      let currentEngagementId;
      if (timeLog) {
        if (timeLog.engagementId !== undefined) {
          currentEngagementId = timeLog.engagementId;
        } else if (timeLog.engagement && timeLog.engagement.id !== undefined) {
          currentEngagementId = timeLog.engagement.id;
        }
      } else {
        currentEngagementId = preselectedEngagementId;
      }

      if (currentEngagementId) {
        const engagement = engagements.find(e => e.id === currentEngagementId);
        if (engagement) {
          setSelectedEngagementRate(engagement.hourlyRate);
          const type = engagement.engagementType || 'hourly';
          setSelectedEngagementType(type);

          // If daily, set hours to 1 automatically
          if (type === 'daily') {
            setValue('hours', 1);
          }
        }
      }
    }
  }, [open, engagements, timeLog, preselectedEngagementId, setValue]);

  // Handle engagement change
  const handleEngagementChange = (engagementId: string) => {
    setValue('engagementId', Number(engagementId));
    if (engagements) {
      const selectedEngagement = engagements.find(e => e.id === Number(engagementId));
      if (selectedEngagement) {
        setSelectedEngagementRate(selectedEngagement.hourlyRate);
        const type = selectedEngagement.engagementType || 'hourly';
        setSelectedEngagementType(type);

        // If daily, set hours to 1 automatically
        if (type === 'daily') {
          setValue('hours', 1);
        }
      }
    }
  };

  // Submit form data
  const onSubmit = async (data: FormValues) => {
    try {
      setIsSubmitting(true);

      // Validation - ensure at least one date is selected
      if (selectedDates.length === 0) {
        toast({
          title: 'No dates selected',
          description: 'Please select at least one date from the calendar',
          variant: 'destructive',
        });
        setIsSubmitting(false);
        return;
      }

      // Use the selected dates
      const datesToProcess = selectedDates;

      // Format the base data
      const baseData = {
        engagementId: Number(data.engagementId),
        hours: Number(data.hours),
        description: data.description === undefined || data.description === "" || data.description.trim() === ''
          ? null
          : data.description.trim()
      };

      console.log('Submitting time logs for', datesToProcess.length, 'date(s)');

      // Create time logs for each date
      const results = [];
      const errors = [];

      for (const date of datesToProcess) {
        try {
          const formattedData = {
            ...baseData,
            date: date,
          };

          const response = await apiRequest(
            isEditMode ? 'PUT' : 'POST',
            isEditMode ? `/api/time-logs/${timeLog.id}` : '/api/time-logs',
            formattedData
          );

          if (!response.ok) {
            const errorData = await response.json();
            console.error('Server validation errors:', errorData);
            errors.push({ date, error: errorData.message || 'Failed to save time log' });
          } else {
            const savedTimeLog = await response.json();
            results.push(savedTimeLog);
            console.log('Time log saved successfully for', date);
          }
        } catch (error) {
          console.error('Error saving time log for', date, ':', error);
          errors.push({ date, error: error instanceof Error ? error.message : 'Unknown error' });
        }
      }

      // Show success toast
      if (results.length > 0) {
        toast({
          title: `Time log${results.length > 1 ? 's' : ''} created successfully`,
          description: results.length > 1
            ? `Created ${results.length} time log entries`
            : `${baseData.hours} hours logged for ${format(datesToProcess[0], 'MMM d, yyyy')}`,
        });
      }

      if (errors.length > 0) {
        toast({
          title: `Failed to create ${errors.length} time log${errors.length > 1 ? 's' : ''}`,
          description: 'Some entries could not be saved',
          variant: 'destructive',
        });
      }

      // Always invalidate ALL time log queries regardless of path to ensure complete refresh
      console.log('Invalidating all time log queries');
      await queryClient.invalidateQueries({
        queryKey: ['/api/time-logs'],
        refetchType: 'all',
      });

      // Invalidate dashboard data as well
      await queryClient.invalidateQueries({
        queryKey: ['/api/dashboard'],
        refetchType: 'all',
      });

      // Explicitly force refetch any active queries
      console.log('Force refetching all queries');
      await queryClient.refetchQueries({
        queryKey: ['/api/time-logs'],
        type: 'all',
      });

      // Reset and close if successful
      if (results.length > 0) {
        reset();
        setMultipleEntries(false);
        setCreatedLogs([]);
        setSelectedDates([]);
        onOpenChange(false);
        onSuccess();
      }
    } catch (error) {
      console.error('Error saving time log:', error);
      toast({
        title: 'Error',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Completely reset all state
    setMultipleEntries(false);
    setCreatedLogs([]);
    setSelectedDates([]);
    reset();
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleClose()} modal={false}>
      <SheetContent className="sm:max-w-[500px] w-full overflow-y-auto" pushed={open}>
        <SheetHeader>
          <SheetTitle>{isEditMode ? 'Edit Time Log' : 'New Time Log'}</SheetTitle>
        </SheetHeader>

        {/* Show warning alert when no engagements are found */}
        {noEngagements && (
          <Alert variant="warning" className="mb-4 bg-amber-50 border-amber-300 mt-4">
            <AlertCircle className="h-4 w-4 text-amber-700" />
            <AlertTitle className="text-amber-800">No active engagements found</AlertTitle>
            <AlertDescription className="text-amber-700">
              You need an active engagement to log time.
              <Button
                variant="link"
                className="p-0 h-auto ml-1 text-amber-800 font-medium underline hover:text-amber-900"
                onClick={() => {
                  onOpenChange(false);
                  window.location.href = '/engagements/new';
                }}
              >
                Create a new engagement
              </Button>.
            </AlertDescription>
          </Alert>
        )}

        {isEngagementsError && (
          <Alert variant="destructive" className="mb-4 mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              There was an error loading your engagements. Please try again later.
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-1 gap-2">
              <Label htmlFor="engagementId" className={formLabelStyles}>
                Engagement
              </Label>
              <Controller
                name="engagementId"
                control={control}
                render={({ field }) => {
                  // Add additional debugging
                  console.log('Field value in Select:', field.value);
                  console.log('Engagements available for dropdown:', engagements.length);

                  // Convert to string for Select component, handling null/undefined case
                  const fieldValue = field.value ? field.value.toString() : '';

                  return (
                    <Select
                      disabled={isLoadingEngagements}
                      value={fieldValue}
                      defaultValue={fieldValue}
                      onValueChange={handleEngagementChange}
                    >
                      <SelectTrigger className={errors.engagementId ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Select an engagement" />
                      </SelectTrigger>
                      <SelectContent>
                        {isLoadingEngagements ? (
                          <SelectItem value="loading" disabled>
                            Loading engagements...
                          </SelectItem>
                        ) : engagements.length === 0 ? (
                          <SelectItem value="no-engagements" disabled>
                            No active engagements found. Please create an active engagement first.
                          </SelectItem>
                        ) : (
                          engagements.map((engagement) => (
                            <SelectItem key={engagement.id} value={engagement.id.toString()}>
                              {engagement.clientName} - {engagement.projectName}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  );
                }}
              />
              {errors.engagementId && (
                <span className="text-xs text-red-500">{errors.engagementId.message}</span>
              )}
              {selectedEngagementRate && (
                <span className="text-xs text-slate-500">
                  Rate: ${selectedEngagementRate}/{selectedEngagementType === 'daily' ? 'day' : 'hour'}
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Date Selection - Always use calendar picker */}
              <div className="grid grid-cols-1 gap-2">
                <Label htmlFor="date" className={formLabelStyles}>
                  Select Dates
                </Label>

                {/* Calendar picker */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={`w-full justify-start text-left font-normal ${selectedDates.length === 0 ? 'text-muted-foreground' : ''
                        }`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedDates.length === 0
                        ? 'Pick dates'
                        : `${selectedDates.length} date${selectedDates.length > 1 ? 's' : ''} selected`}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="multiple"
                      selected={selectedDates}
                      onSelect={(dates) => setSelectedDates(dates || [])}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {selectedEngagementType !== 'daily' && (
                <div className="grid grid-cols-1 gap-2">
                  <Label htmlFor="hours" className={formLabelStyles}>
                    Hours (max 8)
                  </Label>
                  <Input
                    id="hours"
                    type="number"
                    min="0.25"
                    max="8"
                    step="0.25"
                    placeholder="0.00"
                    {...register('hours', { valueAsNumber: true })}
                    className={errors.hours ? 'border-red-500' : ''}
                  />
                  {errors.hours && (
                    <span className="text-xs text-red-500">{errors.hours.message}</span>
                  )}
                </div>
              )}

              {/* Hidden input for daily engagements to ensure form submission works */}
              {selectedEngagementType === 'daily' && (
                <input type="hidden" {...register('hours', { value: 1, valueAsNumber: true })} />
              )}
            </div>

            <div className="grid grid-cols-1 gap-2">
              <Label htmlFor="description" className={formLabelStyles}>
                Description
              </Label>
              <Textarea
                id="description"
                placeholder="Describe the work you did"
                rows={3}
                {...register('description')}
                className={errors.description ? 'border-red-500' : ''}
              />
              {errors.description && (
                <span className="text-xs text-red-500">{errors.description.message}</span>
              )}
            </div>

            {/* Display selected dates */}
            {selectedDates.length > 0 && (
              <div className="grid grid-cols-1 gap-2">
                <Label className={formLabelStyles}>
                  Selected Dates ({selectedDates.length})
                </Label>
                <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-muted/50 max-h-32 overflow-y-auto">
                  {selectedDates
                    .sort((a, b) => a.getTime() - b.getTime())
                    .map((date, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-1 px-2 py-1 bg-primary text-primary-foreground rounded-md text-sm"
                      >
                        <span>{format(date, 'MMM d, yyyy')}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedDates(selectedDates.filter((_, i) => i !== index));
                          }}
                          className="ml-1 hover:bg-primary-foreground/20 rounded-full p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          <SheetFooter className="gap-3 sm:justify-between">
            {!isEditMode && (
              <div className="flex items-center gap-2 mr-auto">
                <input
                  type="checkbox"
                  id="multipleEntries"
                  checked={multipleEntries}
                  onChange={(e) => {
                    setMultipleEntries(e.target.checked);
                    if (!e.target.checked) {
                      setCreatedLogs([]);
                    }
                  }}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="multipleEntries" className={formLabelStyles}>
                  Add multiple entries
                </Label>
              </div>
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {multipleEntries ? 'Done' : 'Cancel'}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || isLoadingEngagements || noEngagements}
              >
                {isSubmitting
                  ? 'Saving...'
                  : multipleEntries
                    ? 'Add Entry'
                    : isEditMode
                      ? 'Save Changes'
                      : 'Save'}
              </Button>
            </div>
          </SheetFooter>
        </form>

        {
          multipleEntries && createdLogs.length > 0 && (
            <div className="mt-4 border-t pt-4">
              <h4 className="text-sm font-medium mb-2">
                Added Entries ({createdLogs.length}):
              </h4>
              <div className="max-h-40 overflow-y-auto space-y-2">
                {createdLogs.map((log, index) => (
                  <div
                    key={`${log.date}-${log.hours}-${index}`}
                    className="text-sm bg-slate-50 p-2 rounded-md"
                  >
                    <span className="font-medium">{log.hours} hours</span>
                    <span className="mx-1">on</span>
                    <span className="font-medium">{log.date}</span>
                    {log.description && (
                      <span className="ml-2 text-gray-500">- {log.description}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )
        }
      </SheetContent >
    </Sheet >
  );
};

export default TimeLogModal;
